import ShopifyLogo from "@/assets/images/platforms/shopify.webp";
import WooCommerceLogo from "@/assets/images/platforms/woocommerce.webp";
import ConnectWooCommerce from "@/components/wocommerce/connectWooCommerce";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import { Avatar, AvatarImage } from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  formatPercentage,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import {
  CampaignAnalyticFragmentFragment,
  EcommercePlatformImpactFragmentFragment,
} from "@repo/graphql/documents/campaign/fragment.generated";
import { Plus } from "lucide-react-native";
import React, { useMemo, useState } from "react";
import { Linking } from "react-native";
import IntegrationCard from "../shared/integrationCard";
import Section from "../shared/section";
import { formatRangeWindow } from "../shared/utils";

const EMPTY_GRADIENT =
  "linear-gradient(135deg, #ecfdf5 0%, #ffffff 60%, #f4efff 100%)";

const PURPLE = "#7c3aed";
const PURPLE_FILL = "rgba(124,58,237,0.12)";

const SHOPIFY_INSTALL_URL = "https://apps.shopify.com/montu";

type PlatformKey = "total" | "shopify" | "woocommerce";

type DayPoint = EcommercePlatformImpactFragmentFragment["daily"][number];

type Props = {
  report: CampaignAnalyticFragmentFragment;
  brandId?: string;
};

const formatPlain = (value: number): string =>
  new Intl.NumberFormat("es-CL").format(Math.round(value));

const isoDay = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

const sumDaily = (a: DayPoint[], b: DayPoint[]): DayPoint[] => {
  const byDay = new Map<string, DayPoint>();
  [...a, ...b].forEach((p) => {
    const prev = byDay.get(p.day);
    if (!prev) {
      byDay.set(p.day, { ...p });
    } else {
      byDay.set(p.day, {
        __typename: "EcommerceDayPointType",
        day: p.day,
        sales: prev.sales + p.sales,
        orders: prev.orders + p.orders,
        addToCarts: prev.addToCarts + p.addToCarts,
        traffic: prev.traffic + p.traffic,
      });
    }
  });
  return Array.from(byDay.values()).sort((x, y) => (x.day < y.day ? -1 : 1));
};

type ImpactView = {
  salesCampaign: number;
  salesBaseline: number;
  ordersCampaign: number;
  ordersBaseline: number;
  addToCartsCampaign: number;
  addToCartsBaseline: number;
  trafficCampaign: number;
  trafficBaseline: number;
  conversionRateCampaign: number;
  conversionRateBaseline: number;
  daily: DayPoint[];
};

const toView = (
  p: EcommercePlatformImpactFragmentFragment | null,
): ImpactView | null => {
  if (!p) return null;
  const salesC = p.salesCampaign;
  const salesB = p.salesBaseline;
  return {
    salesCampaign: salesC.amount,
    salesBaseline: salesB.amount,
    ordersCampaign: p.ordersCampaign,
    ordersBaseline: p.ordersBaseline,
    addToCartsCampaign: p.addToCartsCampaign,
    addToCartsBaseline: p.addToCartsBaseline,
    trafficCampaign: p.trafficCampaign,
    trafficBaseline: p.trafficBaseline,
    conversionRateCampaign: p.conversionRateCampaign,
    conversionRateBaseline: p.conversionRateBaseline,
    daily: p.daily,
  };
};

const sumViews = (
  a: ImpactView | null,
  b: ImpactView | null,
): ImpactView | null => {
  if (!a && !b) return null;
  if (a && !b) return a;
  if (b && !a) return b;
  const x = a!;
  const y = b!;
  const orders = x.ordersCampaign + y.ordersCampaign;
  const ordersBase = x.ordersBaseline + y.ordersBaseline;
  const traffic = x.trafficCampaign + y.trafficCampaign;
  const trafficBase = x.trafficBaseline + y.trafficBaseline;
  return {
    salesCampaign: x.salesCampaign + y.salesCampaign,
    salesBaseline: x.salesBaseline + y.salesBaseline,
    ordersCampaign: orders,
    ordersBaseline: ordersBase,
    addToCartsCampaign: x.addToCartsCampaign + y.addToCartsCampaign,
    addToCartsBaseline: x.addToCartsBaseline + y.addToCartsBaseline,
    trafficCampaign: traffic,
    trafficBaseline: trafficBase,
    conversionRateCampaign: traffic > 0 ? orders / traffic : 0,
    conversionRateBaseline: trafficBase > 0 ? ordersBase / trafficBase : 0,
    daily: sumDaily(x.daily, y.daily),
  };
};

/**
 * A store whose daily series was never collected has nothing to report. Treating
 * it as absent keeps its zeros out of the combined totals and out of the tab bar,
 * and surfaces the reconnect card instead of "vendiste $0".
 */
const withData = (
  p: EcommercePlatformImpactFragmentFragment | null | undefined,
): EcommercePlatformImpactFragmentFragment | null =>
  p && p.hasData ? p : null;

const EcommerceImpactSection = React.memo(({ report, brandId }: Props) => {
  const impact = report.ecommerceImpact;
  const shopify = withData(impact?.shopify);
  const woo = withData(impact?.woocommerce);

  const shopifyView = useMemo(() => toView(shopify), [shopify]);
  const wooView = useMemo(() => toView(woo), [woo]);
  const totalView = useMemo(
    () => sumViews(shopifyView, wooView),
    [shopifyView, wooView],
  );

  // Derived during render rather than seeded into state: which stores report
  // data is only known once the query resolves, and a tab chosen before then
  // would stick to a store the section no longer shows.
  const [selected, setSelected] = useState<PlatformKey | null>(null);
  const isAvailable = (key: PlatformKey) =>
    key === "total"
      ? Boolean(shopify || woo)
      : key === "shopify"
        ? !!shopify
        : !!woo;
  const fallback: PlatformKey =
    shopify && woo
      ? "total"
      : shopify
        ? "shopify"
        : woo
          ? "woocommerce"
          : "total";
  const platform: PlatformKey =
    selected && isAvailable(selected) ? selected : fallback;
  const setPlatform = setSelected;

  if (!impact || !impact.hasData) return null;

  const allDisconnected = !shopify && !woo;
  if (allDisconnected && !brandId) return null;

  const openWoo = () => {
    if (!brandId) return;
    ModalProvider.show({
      children: <ConnectWooCommerce brandId={brandId} />,
    });
  };

  const activeData =
    platform === "shopify"
      ? shopifyView
      : platform === "woocommerce"
        ? wooView
        : totalView;

  const showEmpty = !activeData;

  const campaignRange = formatRangeWindow(
    impact.campaignWindow.start,
    impact.campaignWindow.ongoing ? null : impact.campaignWindow.end,
  );
  const baselineRange = formatRangeWindow(
    impact.baselineWindow.start,
    impact.baselineWindow.end,
  );
  const campaignStartDay = isoDay(impact.campaignWindow.start);

  return (
    <Section
      title="Impacto en tu ecommerce"
      description="Comparación de ventas, órdenes, add-to-cart y tráfico durante la campaña vs los 14 días previos al primer post."
    >
      <HStack space="lg" className="items-stretch">
        <SideRail
          shopify={shopify}
          woo={woo}
          platform={platform}
          onPlatform={setPlatform}
          baselineRange={baselineRange}
          campaignRange={campaignRange}
          brandId={brandId}
          openWoo={openWoo}
        />
        <Box className="flex-1 min-w-0">
          {showEmpty ? (
            <PlatformEmpty platform={platform} openWoo={openWoo} />
          ) : (
            <Grid data={activeData!} campaignStartDay={campaignStartDay} />
          )}
        </Box>
      </HStack>
    </Section>
  );
});

EcommerceImpactSection.displayName = "EcommerceImpactSection";

const openExternal = (url: string) => {
  if (!url) return;
  if (typeof window !== "undefined") {
    window.open(url, "_blank");
    return;
  }
  Linking.openURL(url);
};

const SideRail = ({
  shopify,
  woo,
  platform,
  onPlatform,
  baselineRange,
  campaignRange,
  brandId,
  openWoo,
}: {
  shopify: EcommercePlatformImpactFragmentFragment | null;
  woo: EcommercePlatformImpactFragmentFragment | null;
  platform: PlatformKey;
  onPlatform: (p: PlatformKey) => void;
  baselineRange: string;
  campaignRange: string;
  brandId?: string;
  openWoo: () => void;
}) => {
  return (
    <VStack
      space="md"
      className="bg-background-50 rounded-2xl p-5 border border-outline-100"
      style={{ width: 260, flexShrink: 0 } as object}
    >
      <Text
        size="2xs"
        className="text-typography-500 uppercase font-bold tracking-wide"
      >
        Tiendas analizadas
      </Text>
      <AccountRow
        logo={ShopifyLogo}
        platform={shopify}
        fallbackHandle="Sin Shopify"
        connectLabel="Conectar Shopify"
        onConnect={
          brandId ? () => openExternal(SHOPIFY_INSTALL_URL) : undefined
        }
      />
      <AccountRow
        logo={WooCommerceLogo}
        platform={woo}
        fallbackHandle="Sin WooCommerce"
        connectLabel="Conectar WooCommerce"
        onConnect={brandId ? openWoo : undefined}
      />

      <Box className="h-px bg-outline-100 my-1" />

      <Text
        size="2xs"
        className="text-typography-500 uppercase font-bold tracking-wide"
      >
        Mostrando
      </Text>
      <PlatformToggle
        value={platform}
        onChange={onPlatform}
        hasShopify={!!shopify}
        hasWoo={!!woo}
      />

      <Box className="h-px bg-outline-100 my-1" />

      <VStack space="xs">
        <Text
          size="2xs"
          className="text-typography-500 uppercase font-bold tracking-wide"
        >
          Baseline
        </Text>
        <Text size="xs" className="text-typography-700">
          {baselineRange}
        </Text>
      </VStack>
      <VStack space="xs">
        <Text
          size="2xs"
          className="text-typography-500 uppercase font-bold tracking-wide"
        >
          Campaña
        </Text>
        <Text size="xs" className="text-typography-700">
          {campaignRange}
        </Text>
      </VStack>
    </VStack>
  );
};

const AccountRow = ({
  logo,
  platform,
  fallbackHandle,
  connectLabel,
  onConnect,
}: {
  logo: number;
  platform: EcommercePlatformImpactFragmentFragment | null;
  fallbackHandle: string;
  connectLabel: string;
  onConnect?: () => void;
}) => {
  const connected = !!platform;
  return (
    <HStack space="sm" className="items-center">
      <Avatar size="sm">
        <AvatarImage source={logo} />
      </Avatar>
      <VStack className="flex-1 min-w-0">
        <Text
          size="xs"
          className={
            connected
              ? "text-typography-900 font-semibold"
              : "text-typography-400"
          }
          numberOfLines={1}
        >
          {connected ? platform!.handle : fallbackHandle}
        </Text>
        <Text size="2xs" className="text-typography-500">
          {connected ? "Conectado" : "No conectado"}
        </Text>
      </VStack>
      {!connected && onConnect ? (
        <Button
          size="xs"
          rounded="full"
          variant="solid"
          action="brand"
          onPress={onConnect}
          aria-label={connectLabel}
          className="w-7 h-7 p-0"
        >
          <ButtonIcon as={Plus} />
        </Button>
      ) : null}
    </HStack>
  );
};

const PlatformToggle = ({
  value,
  onChange,
  hasShopify,
  hasWoo,
}: {
  value: PlatformKey;
  onChange: (p: PlatformKey) => void;
  hasShopify: boolean;
  hasWoo: boolean;
}) => {
  const options: {
    key: PlatformKey;
    label: string;
    disabled: boolean;
    tooltip?: string;
  }[] = [
    {
      key: "total",
      label: "Total",
      disabled: !hasShopify && !hasWoo,
      tooltip:
        !hasShopify && !hasWoo
          ? "Conecta una tienda para ver el total."
          : undefined,
    },
    {
      key: "shopify",
      label: "Shopify",
      disabled: !hasShopify,
      tooltip: !hasShopify ? "Aprieta el + para conectar Shopify" : undefined,
    },
    {
      key: "woocommerce",
      label: "Woo",
      disabled: !hasWoo,
      tooltip: !hasWoo ? "Aprieta el + para conectar WooCommerce" : undefined,
    },
  ];
  return (
    <HStack
      space="xs"
      className="bg-background-0 p-1 rounded-lg border border-outline-100"
    >
      {options.map((opt) => {
        const active = value === opt.key;
        const button = (
          <Pressable
            onPress={() => !opt.disabled && onChange(opt.key)}
            className={
              "w-full px-2 py-1.5 rounded-md " +
              (active && !opt.disabled
                ? "bg-brand-500"
                : opt.disabled
                  ? "bg-background-50"
                  : "bg-transparent")
            }
            disabled={opt.disabled}
          >
            <Text
              size="xs"
              className={
                "text-center font-semibold " +
                (active && !opt.disabled
                  ? "text-white"
                  : opt.disabled
                    ? "text-typography-300"
                    : "text-typography-700")
              }
            >
              {opt.label}
            </Text>
          </Pressable>
        );
        if (opt.tooltip) {
          return (
            <Tooltip
              key={opt.key}
              placement="top"
              trigger={(triggerProps) => (
                <Box {...triggerProps} className="flex-1">
                  {button}
                </Box>
              )}
            >
              <TooltipContent>
                <TooltipText>{opt.tooltip}</TooltipText>
              </TooltipContent>
            </Tooltip>
          );
        }
        return (
          <Box key={opt.key} className="flex-1">
            {button}
          </Box>
        );
      })}
    </HStack>
  );
};

const Grid = ({
  data,
  campaignStartDay,
}: {
  data: ImpactView;
  campaignStartDay: string | null;
}) => {
  return (
    <VStack space="md">
      <HStack space="md" className="flex-wrap">
        <ImpactCard
          eyebrow="Ventas"
          primary={`$${formatSeparators(data.salesCampaign)}`}
          baseline={`$${formatSeparators(data.salesBaseline)} línea base`}
          delta={formatPctDelta(data.salesCampaign, data.salesBaseline)}
          deltaPositive={data.salesCampaign >= data.salesBaseline}
          chart={
            <Sparkline
              points={data.daily.map((d) => ({ day: d.day, value: d.sales }))}
              campaignStartDay={campaignStartDay}
            />
          }
        />
        <ImpactCard
          eyebrow="Órdenes"
          primary={formatPlain(data.ordersCampaign)}
          baseline={`${formatPlain(data.ordersBaseline)} línea base`}
          delta={formatPctDelta(data.ordersCampaign, data.ordersBaseline)}
          deltaPositive={data.ordersCampaign >= data.ordersBaseline}
          chart={
            <Sparkline
              points={data.daily.map((d) => ({ day: d.day, value: d.orders }))}
              campaignStartDay={campaignStartDay}
            />
          }
        />
      </HStack>
      <HStack space="md" className="flex-wrap">
        <ImpactCard
          eyebrow="Add to cart"
          primary={formatPlain(data.addToCartsCampaign)}
          baseline={`${formatPlain(data.addToCartsBaseline)} línea base`}
          delta={formatPctDelta(
            data.addToCartsCampaign,
            data.addToCartsBaseline,
          )}
          deltaPositive={data.addToCartsCampaign >= data.addToCartsBaseline}
          chart={
            <Sparkline
              points={data.daily.map((d) => ({
                day: d.day,
                value: d.addToCarts,
              }))}
              campaignStartDay={campaignStartDay}
            />
          }
        />
        <ImpactCard
          eyebrow="Tráfico"
          primary={formatPlain(data.trafficCampaign)}
          baseline={`${formatPlain(data.trafficBaseline)} línea base`}
          delta={formatPctDelta(data.trafficCampaign, data.trafficBaseline)}
          deltaPositive={data.trafficCampaign >= data.trafficBaseline}
          chart={
            <Sparkline
              points={data.daily.map((d) => ({ day: d.day, value: d.traffic }))}
              campaignStartDay={campaignStartDay}
            />
          }
        />
      </HStack>
      <Text size="2xs" className="text-typography-400">
        Conversión: {formatPercentage(data.conversionRateCampaign)} en campaña ·{" "}
        {formatPercentage(data.conversionRateBaseline)} en línea base
      </Text>
    </VStack>
  );
};

const ImpactCard = ({
  eyebrow,
  primary,
  baseline,
  delta,
  deltaPositive,
  chart,
}: {
  eyebrow: string;
  primary: string;
  baseline: string;
  delta: string;
  deltaPositive: boolean;
  chart?: React.ReactNode;
}) => (
  <Card
    variant="outline"
    className="rounded-xl flex-1"
    size="md"
    style={{ minWidth: 240 } as object}
  >
    <VStack space="sm">
      <Text
        size="2xs"
        className="text-typography-500 uppercase font-bold tracking-wide"
      >
        {eyebrow}
      </Text>
      <Heading size="2xl" className="text-typography-900">
        {primary}
      </Heading>
      <HStack space="sm" className="items-baseline flex-wrap">
        <Text size="xs" className="text-typography-500">
          {baseline}
        </Text>
        <Text
          size="xs"
          className={
            "font-semibold " +
            (deltaPositive ? "text-success-600" : "text-error-600")
          }
        >
          {deltaPositive ? "↑" : "↓"} {delta}
        </Text>
      </HStack>
      {chart ? <Box className="mt-2">{chart}</Box> : null}
    </VStack>
  </Card>
);

type SparkPoint = { day: string; value: number };

const Sparkline = ({
  points,
  campaignStartDay,
}: {
  points: SparkPoint[];
  campaignStartDay?: string | null;
}) => {
  if (points.length < 2) return null;
  const width = 320;
  const height = 72;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = height - ((p.value - min) / range) * (height - 6) - 3;
    return { x, y };
  });
  const polyline = coords.map((p) => `${p.x},${p.y}`).join(" ");
  const area = `M ${coords[0]!.x},${height} L ${coords
    .map((p) => `${p.x},${p.y}`)
    .join(" L ")} L ${coords[coords.length - 1]!.x},${height} Z`;
  const dashedX = (() => {
    if (!campaignStartDay) return null;
    const idx = points.findIndex((p) => p.day >= campaignStartDay);
    if (idx < 0) return null;
    return (idx / (points.length - 1)) * width;
  })();
  return (
    <Box className="w-full" style={{ height } as object}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
      >
        <path d={area} fill={PURPLE_FILL} stroke="none" />
        <polyline
          fill="none"
          stroke={PURPLE}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={polyline}
        />
        {dashedX !== null ? (
          <line
            x1={dashedX}
            x2={dashedX}
            y1={0}
            y2={height}
            stroke="#9ca3af"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        ) : null}
      </svg>
    </Box>
  );
};

const PlatformEmpty = ({
  platform,
  openWoo,
}: {
  platform: PlatformKey;
  openWoo: () => void;
}) => {
  if (platform === "shopify") {
    return (
      <IntegrationCard
        heading="Conecta Shopify para medir el impacto en tus ventas"
        description="Vincula tu tienda Shopify para cruzar ventas, órdenes, add-to-cart y tráfico contra la ventana de la campaña."
        gradient={EMPTY_GRADIENT}
        ctas={[{ label: "Conectar Shopify", href: SHOPIFY_INSTALL_URL }]}
        logos={[ShopifyLogo]}
      />
    );
  }
  if (platform === "woocommerce") {
    return (
      <IntegrationCard
        heading="Conecta WooCommerce para medir el impacto en tus ventas"
        description="Vincula tu tienda WooCommerce para cruzar ventas, órdenes, add-to-cart y tráfico contra la ventana de la campaña."
        gradient={EMPTY_GRADIENT}
        ctas={[{ label: "Conectar WooCommerce", onPress: openWoo }]}
        logos={[WooCommerceLogo]}
      />
    );
  }
  return (
    <IntegrationCard
      heading="Mide el impacto real en tus ventas"
      description="Integra tu tienda para correlacionar ingresos, órdenes y sesiones contra la ventana de la campaña."
      gradient={EMPTY_GRADIENT}
      ctas={[
        { label: "Conectar Shopify", href: SHOPIFY_INSTALL_URL },
        { label: "Conectar WooCommerce", onPress: openWoo },
      ]}
      logos={[ShopifyLogo, WooCommerceLogo]}
    />
  );
};

const formatPctDelta = (current: number, baseline: number): string => {
  if (baseline <= 0) {
    return current > 0 ? "+∞" : "0 %";
  }
  const ratio = (current - baseline) / baseline;
  return formatPercentage(Math.abs(ratio));
};

export default EcommerceImpactSection;
